import { X, User, EnvelopeSimple, Phone, MapPin, Package, CurrencyDollar, Truck, ArrowClockwise } from "@phosphor-icons/react";
import { useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";

function fmtAddress(addr) {
  if (!addr) return null;
  const parts = [addr.line1, addr.line2, addr.postal_code + " " + (addr.city || ""), addr.state, addr.country]
    .filter((s) => s && s.trim() !== "" && s.trim() !== ",");
  return parts.join(", ");
}

export default function OrderDetailsModal({ order: initialOrder, onClose, showAllItems = false }) {
  const [order, setOrder] = useState(initialOrder);
  const [syncing, setSyncing] = useState(false);
  if (!order) return null;
  const cd = order.customer_details || {};
  const sd = order.shipping_details || {};
  const shipAddr = sd.address || cd.address;
  const billAddr = cd.address;
  const items = order.items || [];
  const total = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const shipping = order.shipping || 0;

  const syncShipping = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/sync-shipping`);
      setOrder({ ...order, customer_details: data.customer_details, shipping_details: data.shipping_details });
      if (data.shipping_details?.address?.line1) {
        toast.success("Adresse récupérée depuis Stripe");
      } else {
        toast.info("Aucune adresse disponible sur Stripe pour cette commande");
      }
    } catch (e) {
      toast.error("Impossible de re-synchroniser");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="order-details-modal">
      <div className="bg-[#0A0A0A] border border-white/20 w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-white/10 flex items-center justify-between sticky top-0 bg-[#0A0A0A] z-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#FF3B00]">Commande</div>
            <div className="font-display text-3xl uppercase">#{order.id?.slice(-8).toUpperCase()}</div>
            <div className="text-xs text-white/40 mt-1">{new Date(order.created_at).toLocaleString("fr-FR")}</div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="close-order-modal"><X size={24} /></button>
        </div>

        <div className="p-6 space-y-6">
          {/* Client */}
          <div>
            <div className="text-xs uppercase tracking-widest text-[#FF3B00] mb-3 flex items-center gap-2">
              <User size={14} weight="bold" /> Client
            </div>
            <div className="border border-white/10 p-4 grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs uppercase tracking-widest text-white/40 flex items-center gap-1"><User size={12} /> Nom</div>
                <div className="mt-1">{cd.name || sd.name || "—"}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-white/40 flex items-center gap-1"><EnvelopeSimple size={12} /> Courriel</div>
                <div className="mt-1 break-all">
                  <a href={`mailto:${cd.email || order.buyer_email}`} className="hover:text-[#FF3B00]">
                    {cd.email || order.buyer_email || "—"}
                  </a>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-white/40 flex items-center gap-1"><Phone size={12} /> Téléphone</div>
                <div className="mt-1">
                  {cd.phone ? (
                    <a href={`tel:${cd.phone}`} className="hover:text-[#FF3B00]">{cd.phone}</a>
                  ) : "—"}
                </div>
              </div>
            </div>
          </div>

          {/* Shipping address */}
          <div>
            <div className="text-xs uppercase tracking-widest text-[#FF3B00] mb-3 flex items-center gap-2">
              <Truck size={14} weight="bold" /> Adresse de livraison
            </div>
            <div className="border border-white/10 p-4 text-sm">
              {shipAddr ? (
                <>
                  {sd.name && <div className="font-medium">{sd.name}</div>}
                  <div className="mt-1 text-white/80">
                    {shipAddr.line1}
                    {shipAddr.line2 && <><br />{shipAddr.line2}</>}
                    <br />
                    {shipAddr.postal_code} {shipAddr.city}
                    {shipAddr.state && `, ${shipAddr.state}`}
                    <br />
                    {shipAddr.country}
                  </div>
                  {sd.phone && (
                    <div className="mt-2 text-xs text-white/50 flex items-center gap-1">
                      <Phone size={12} /> {sd.phone}
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-3">
                  <div className="text-white/40 italic">Adresse non fournie. Essayez de re-synchroniser avec Stripe.</div>
                  <button
                    onClick={syncShipping}
                    disabled={syncing}
                    data-testid="sync-shipping-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-[#FF3B00] text-[#FF3B00] uppercase tracking-widest text-xs hover:bg-[#FF3B00] hover:text-white disabled:opacity-50"
                  >
                    <ArrowClockwise size={14} className={syncing ? "animate-spin" : ""} />
                    {syncing ? "Récupération..." : "Récupérer depuis Stripe"}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Billing address (if different) */}
          {billAddr && billAddr.line1 && billAddr.line1 !== shipAddr?.line1 && (
            <div>
              <div className="text-xs uppercase tracking-widest text-[#FF3B00] mb-3 flex items-center gap-2">
                <MapPin size={14} weight="bold" /> Adresse de facturation
              </div>
              <div className="border border-white/10 p-4 text-sm text-white/80">
                {billAddr.line1}
                {billAddr.line2 && <><br />{billAddr.line2}</>}
                <br />
                {billAddr.postal_code} {billAddr.city}
                {billAddr.state && `, ${billAddr.state}`}
                <br />
                {billAddr.country}
              </div>
            </div>
          )}

          {/* Items */}
          <div>
            <div className="text-xs uppercase tracking-widest text-[#FF3B00] mb-3 flex items-center gap-2">
              <Package size={14} weight="bold" /> Articles ({items.length})
            </div>
            <div className="border border-white/10 divide-y divide-white/5">
              {items.map((it, i) => (
                <div key={i} className="p-3 flex items-center gap-3">
                  <div className="w-14 h-14 bg-[#111] flex-shrink-0">
                    {it.image && <img src={fileUrl(it.image)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    {showAllItems && (
                      <div className="text-xs uppercase tracking-widest text-white/40">{it.artist_name}</div>
                    )}
                    <div className="font-medium truncate">{it.name}</div>
                    <div className="text-xs text-white/50 mt-0.5">
                      {it.size && <span className="mr-2">Taille: {it.size}</span>}
                      {it.color && <span className="mr-2">Couleur: {it.color}</span>}
                      <span>× {it.quantity}</span>
                    </div>
                  </div>
                  <div className="font-display text-xl">${(it.price * it.quantity).toFixed(2)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="border-t border-white/10 pt-4 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-white/60">Sous-total</span><span>${total.toFixed(2)}</span></div>
            {shipping > 0 && (
              <div className="flex justify-between"><span className="text-white/60">Livraison</span><span>${shipping.toFixed(2)}</span></div>
            )}
            <div className="flex justify-between font-display text-2xl border-t border-white/10 pt-2 mt-2">
              <span>Total</span>
              <span>${(total + (showAllItems ? shipping : 0)).toFixed(2)} <span className="text-xs text-white/40 tracking-widest">CAD</span></span>
            </div>
          </div>

          {/* Status */}
          <div className="border border-white/10 p-4 flex items-center justify-between text-sm">
            <div>
              <div className="text-xs uppercase tracking-widest text-white/40">Statut</div>
              <div className="mt-1 flex gap-2">
                <span className={`text-xs uppercase px-2 py-1 ${order.payment_status === "paid" ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}`}>
                  {order.payment_status === "paid" ? "Payé" : order.payment_status}
                </span>
                <span className={`text-xs uppercase px-2 py-1 ${order.shipping_status === "shipped" ? "bg-green-500/20 text-green-400" : "bg-[#FF3B00]/20 text-[#FF3B00]"}`}>
                  {order.shipping_status === "shipped" ? "Expédié" : "À préparer"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
